'use client'

import { useT, type TranslateFn, useFormat } from '@/lib/i18n'

import { useState } from 'react'
import { ArrowLeft, X } from 'lucide-react'
import { useAnomalyDetail } from '../anomalies.hooks'
import type { AnomalyDetail, SimilarCase, TransportChain } from '../anomalies.types'
import { formatSchemeProbability, SCHEME_PROBABILITY_LABEL_KEY } from '../scheme-probability'

function scoreColor(s: number): string {
  if (s >= 7) return '#ef4444'
  if (s >= 3) return '#f59e0b'
  return '#22c55e'
}

function esc(v: unknown): string {
  if (v == null) return '—'
  return String(v)
}

// A.Reasons понятным языком (те же подписи, что и в таблице аномалий).
// API-поля a_reasons/short_causes не меняются.
function humanReasons(raw: string | undefined, t: TranslateFn): string {
  if (!raw) return ''
  return raw
    .split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .map(seg => {
      if (seg === 'IF') return t('analytics.method.atypicalCombo')
      if (seg === 'Z') return t('analytics.method.deviationFromMean')
      if (seg === 'IQR') return t('analytics.method.outOfRange')
      if (/^Δвес/i.test(seg)) return seg.replace(/^Δвес/i, t('analytics.method.weightDiff'))
      if (/^инт\./i.test(seg)) return seg.replace(/^инт\./i, t('analytics.method.interval'))
      return seg
    })
    .join('; ')
}

interface Props {
  caseId: string | null
  onClose: () => void
}

export function AnomalyDetailDrawer({ caseId, onClose }: Props) {
  const t = useT()
  // caseId — выбранный из таблицы кейс (initial). Навигация по похожим кейсам
  // живёт внутри drawer: currentCaseId — что показываем сейчас, historyStack —
  // стек переходов для кнопки «Назад».
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(caseId)
  const [prevCaseId, setPrevCaseId] = useState<string | null>(caseId)
  const [historyStack, setHistoryStack] = useState<string[]>([])

  // Родитель выбрал другой кейс из таблицы → начинаем заново, историю сбрасываем.
  if (caseId !== prevCaseId) {
    setPrevCaseId(caseId)
    setCurrentCaseId(caseId)
    setHistoryStack([])
  }

  const { data: d, isLoading, isError } = useAnomalyDetail(currentCaseId)

  if (!caseId) return null

  function openRelatedCase(nextCaseId: string) {
    if (!currentCaseId || nextCaseId === currentCaseId) return
    setHistoryStack(prev => [...prev, currentCaseId])
    setCurrentCaseId(nextCaseId)
  }

  function goBack() {
    setHistoryStack(prev => {
      const previous = prev[prev.length - 1]
      if (previous) setCurrentCaseId(previous)
      return prev.slice(0, -1)
    })
  }

  const backTarget = historyStack[historyStack.length - 1]

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[860px] flex-col overflow-y-auto bg-[var(--bg-canvas)] shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[var(--border-default)] bg-[var(--bg-canvas)] px-6 py-4">
          <div className="min-w-0 flex flex-col gap-1.5">
            {backTarget && (
              <button
                type="button"
                onClick={goBack}
                className="flex w-fit items-center gap-1 text-[11px] font-medium text-[var(--accent-data)] hover:underline"
              >
                <ArrowLeft size={12} />
                Назад к {backTarget}
              </button>
            )}
            <h2 className="truncate text-[16px] font-bold text-[var(--text-primary)]">
              {currentCaseId}
            </h2>
            {historyStack.length > 0 && (
              <p className="truncate text-[10px] text-[var(--text-tertiary)]">
                История переходов: {[...historyStack, currentCaseId].join(' → ')}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-5 px-6 py-5">
          {isLoading && <p className="py-12 text-center text-[var(--text-tertiary)]">{t('analytics.drawer.loadingExplanation')}</p>}
          {isError && !isLoading && (
            <p className="py-12 text-center text-[var(--text-tertiary)]">
              Не удалось загрузить объяснение для {currentCaseId}
            </p>
          )}
          {d && !isLoading && <DrawerContent d={d} onOpenRelated={openRelatedCase} />}
        </div>
      </div>
    </>
  )
}

function DrawerContent({ d, onOpenRelated }: { d: AnomalyDetail; onOpenRelated: (caseId: string) => void }) {
  return (
    <>
      <NarrativeSection d={d} />
      <HiddenTransitIndicatorsSection d={d} />
      <StatisticalDeviationsSection d={d} />
      <ConfidenceReducersSection d={d} />
      <MlAnomalySection d={d} />
      <ScoringSection d={d} />
      <MethodsSection d={d} />
      <TransportChainSection chain={d.transport_chain} />
      <SimilarCasesSection cases={d.similar_cases} onOpenRelated={onOpenRelated} />
      <RuleScenarioSection d={d} />
      <ScenarioSection d={d} />
      <CaseDetailsSection d={d} />
      <ChecklistSection d={d} />
      <RecommendationSection d={d} />
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)]">
      <div className="border-b border-[var(--border-default)] px-4 py-2.5">
        <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">{title}</h3>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  )
}

function KV({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-baseline gap-2 py-0.5">
      <span className="shrink-0 text-[11px] text-[var(--text-tertiary)]">{label}</span>
      <span className={`text-[12px] ${highlight ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
        {value}
      </span>
    </div>
  )
}

function NarrativeSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  return (
    <Section title={t('analytics.section.narrative')}>
      <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">{d.narrative}</p>
    </Section>
  )
}

function MlAnomalySection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const link = d.ml_anomaly_link
  const colorMap: Record<string, string> = {
    red: '#ef4444', orange: '#f59e0b', blue: '#3b82f6', green: '#22c55e',
  }
  const c = colorMap[link.color] || '#888'
  return (
    <Section title="ML Score vs Anomaly Score">
      <div className="flex items-center gap-6">
        <div className="text-center">
          <p className="text-[10px] font-medium uppercase text-[var(--text-tertiary)]">{t('analytics.section.mlAnomaly')}</p>
          <p className="text-[28px] font-bold" style={{ color: scoreColor(d.anomaly_score) }}>{d.anomaly_score.toFixed(1)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-medium uppercase text-[var(--text-tertiary)]">{t('analytics.section.mlRisk')}</p>
          <p className="text-[28px] font-bold" style={{ color: d.ml_risk_score != null ? scoreColor(d.ml_risk_score) : '#888' }}>
            {d.ml_risk_score != null ? d.ml_risk_score.toFixed(1) : '—'}
          </p>
        </div>
      </div>
      <div className="mt-3 rounded-[4px] px-3 py-2" style={{ background: `${c}14`, borderLeft: `3px solid ${c}` }}>
        <p className="text-[11px] font-semibold" style={{ color: c }}>{link.label}</p>
        <p className="mt-0.5 text-[11px] text-[var(--text-secondary)]">{link.text}</p>
      </div>
    </Section>
  )
}

function ScoringSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const f = useFormat()
  return (
    <Section title={t('analytics.section.scoring')}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <KV label="Isolation Forest:" value={d.iforest_score != null ? d.iforest_score.toFixed(1) : '—'} />
        <KV label={t('analytics.scoring.statistical')} value={d.stat_score != null ? d.stat_score.toFixed(1) : '—'} />
        <KV label={t('analytics.scoring.finalAnomaly')} value={d.anomaly_score.toFixed(1)} highlight />
        <KV label={t('analytics.scoring.mlRisk')} value={d.ml_risk_score != null ? d.ml_risk_score.toFixed(1) : '—'} />
      </div>
      {/* Вероятность схемной модели реестра — отдельная строка и отдельная
          подпись. Раньше эта величина приходила под именем ml_risk_score и
          подменяла балл витрины выше; теперь обе видны рядом и не смешиваются. */}
      {d.scheme_model_probability !== undefined && (
        <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
          <KV
            label={`${t(SCHEME_PROBABILITY_LABEL_KEY)}:`}
            value={formatSchemeProbability(d.scheme_model_probability, f)}
          />
          {d.scheme_model && <KV label={t('analytics.scoring.schemeModel')} value={d.scheme_model.name} />}
        </div>
      )}
    </Section>
  )
}

function MethodsSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  return (
    <Section title={t('analytics.section.methods')}>
      <div className="space-y-2">
        {d.methods.map((m, i) => (
          <div key={i} className="rounded-[4px] border border-[var(--border-default)] p-3">
            <div className="flex items-center gap-2">
              <span className="rounded bg-[var(--accent-data)] px-1.5 py-0.5 text-[10px] font-bold text-white">{m.name}</span>
              {m.score != null && (
                <span className="text-[11px] font-semibold" style={{ color: scoreColor(m.score) }}>{m.score.toFixed(1)}</span>
              )}
            </div>
            <p className="mt-1.5 whitespace-pre-line text-[11px] leading-relaxed text-[var(--text-secondary)]">{m.description}</p>
          </div>
        ))}
      </div>
    </Section>
  )
}

function HiddenTransitIndicatorsSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const items = d.hidden_transit_indicators ?? []
  if (!items.length) return null
  return (
    <Section title={t('analytics.section.indicators')}>
      <div className="space-y-1.5">
        {items.map(f => (
          <div key={f.key} className="flex items-center gap-3 rounded-[4px] px-3 py-2" style={{ background: 'rgba(34,197,94,.06)' }}>
            <span style={{ color: '#22c55e' }}>✓</span>
            <span className="text-[12px] font-medium text-[var(--text-primary)]">{f.label}</span>
            {f.value != null && <span className="text-[12px] font-bold text-[var(--text-primary)]">{f.value}</span>}
          </div>
        ))}
      </div>
    </Section>
  )
}

function StatisticalDeviationsSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const items = d.statistical_deviations ?? []
  if (!items.length) return null
  const sevColor: Record<string, string> = { high: '#ef4444', medium: '#f59e0b', low: '#888' }
  return (
    <Section title={t('analytics.section.deviations')}>
      <div className="space-y-2">
        {items.map(dv => (
          <div key={dv.key} className="flex items-start gap-3 rounded-[4px] px-3 py-2" style={{ background: `${sevColor[dv.severity] || '#888'}0a` }}>
            <span className="mt-0.5 text-[12px]" style={{ color: sevColor[dv.severity] || '#888' }}>!</span>
            <div className="flex-1">
              <p className="whitespace-pre-line text-[12px] leading-relaxed text-[var(--text-primary)]">
                <span className="font-medium">{dv.label}</span>
                {dv.value != null && <span className="ml-1 font-bold">{dv.value}</span>}
              </p>
              {dv.method && <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">{dv.method}</p>}
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}

function ConfidenceReducersSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const items = d.confidence_reducers ?? []
  if (!items.length) return null
  return (
    <Section title={t('analytics.section.confidenceReducers')}>
      <div className="space-y-1.5">
        {items.map(f => (
          <div key={f.key} className="flex items-center gap-3 rounded-[4px] px-3 py-2" style={{ background: 'rgba(136,136,136,.06)' }}>
            <span style={{ color: '#888' }}>✗</span>
            <span className="text-[12px] font-medium text-[var(--text-secondary)]">{f.label}</span>
            {f.value != null && <span className="text-[12px] font-bold text-[var(--text-secondary)]">{f.value}</span>}
          </div>
        ))}
      </div>
    </Section>
  )
}

function TransportChainSection({ chain }: { chain: TransportChain }) {
  const t = useT()
  const il = chain.import_leg
  const jn = chain.junction
  const el = chain.export_leg

  return (
    <Section title={t('analytics.section.transportChain')}>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
        {/* Import leg */}
        <div className="rounded-[6px] border border-[var(--border-default)] p-3">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">{t('analytics.chain.importLeg')}</p>
          <p className="mt-1 text-[16px] font-bold text-[var(--text-primary)]">
            {esc(il.country_from)} → КЗ
          </p>
          <div className="mt-2 space-y-0.5">
            <KV label={t('analytics.chain.shipment')} value={esc(il.shipment_id)} />
            <KV label={t('analytics.chain.date')} value={esc(il.date)} />
            <KV label={t('analytics.chain.depStationData')} value={esc(il.station_from)} />
            <KV label={t('analytics.chain.arrStationKz')} value={esc(il.station_to)} />
            {il.border_in && <KV label={t('analytics.chain.borderIn')} value={esc(il.border_in)} />}
          </div>
        </div>

        {/* Junction */}
        <div className="flex min-w-[140px] flex-col items-center justify-center rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{t('analytics.chain.junctionInKz')}</p>
          <p className="mt-2 text-[13px] font-bold text-[var(--text-primary)]">
            {esc(jn.import_station)} → {esc(jn.export_station)}
          </p>
          <p className="mt-1.5 text-[12px] font-semibold" style={{ color: jn.same_station ? '#22c55e' : '#f59e0b' }}>
            {t('analytics.details.stationMatches')} {jn.same_station === true ? t('common.value.yes') : jn.same_station === false ? t('common.value.no') : '—'}
          </p>
          {jn.days_between != null && (
            <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">Интервал: {jn.days_between} дн.</p>
          )}
        </div>

        {/* Export leg */}
        <div className="rounded-[6px] border border-[var(--border-default)] p-3">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">{t('analytics.chain.exportLeg')}</p>
          <p className="mt-1 text-[16px] font-bold text-[var(--text-primary)]">
            КЗ → {esc(el.country_to)}
          </p>
          <div className="mt-2 space-y-0.5">
            <KV label={t('analytics.chain.shipment')} value={esc(el.shipment_id)} />
            <KV label={t('analytics.chain.date')} value={esc(el.date)} />
            <KV label={t('analytics.chain.depStationKz')} value={esc(el.station_from)} />
            <KV label={t('analytics.chain.destStationData')} value={esc(el.station_to)} />
            {el.border_out && <KV label={t('analytics.chain.borderOut')} value={esc(el.border_out)} />}
          </div>
        </div>
      </div>
    </Section>
  )
}

function SimilarCasesSection({ cases, onOpenRelated }: { cases: SimilarCase[]; onOpenRelated: (caseId: string) => void }) {
  const t = useT()
  const [showAll, setShowAll] = useState(false)
  if (!cases.length) return null
  const visible = showAll ? cases : cases.slice(0, 5)
  const hiddenCount = cases.length - 5

  return (
    <Section title={t('analytics.similar.title', { count: cases.length })}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] border-collapse text-[11px]">
          <thead>
            <tr className="border-b border-[var(--border-default)]">
              {/* key — стабильный идентификатор колонки, НЕ перевод: иначе React
                  пересоздавал бы всю строку заголовков при смене языка. */}
              {([
                ['ID', 'ID'], ['importDate', t('analytics.col.importDate')],
                ['exportDate', t('analytics.col.exportDate')], ['wagon', t('analytics.col.wagon')],
                ['importShipment', t('analytics.col.importShipment')],
                ['exportShipment', t('analytics.col.exportShipment')], ['route', t('analytics.col.route')],
                ['importStation', t('analytics.col.importStation')],
                ['exportStation', t('analytics.col.exportStation')],
                ['payerImport', t('analytics.col.payerCodeImport')],
                ['payerExport', t('analytics.col.payerCodeExport')],
                ['weightImport', t('analytics.col.weightImport')],
                ['weightExport', t('analytics.col.weightExport')],
                ['weightDelta', t('analytics.col.weightDelta')], ['days', t('analytics.col.days')],
                ['aScore', 'A.Score'], ['ml', 'ML'],
                ['diff', t('analytics.col.diffFromCurrent')], ['methods', t('analytics.col.methods')],
                ['aReasons', 'A.Reasons'],
              ] as const).map(([k, h]) => (
                <th key={k} className="whitespace-nowrap px-2 py-1.5 text-left text-[9px] font-medium uppercase text-[var(--text-tertiary)]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(c => {
              const iw = c.import_weight != null ? c.import_weight.toFixed(1) : '—'
              const ew = c.export_weight != null ? c.export_weight.toFixed(1) : '—'
              const wd = c.weight_diff_pct != null ? `${c.weight_diff_pct.toFixed(1)}%` : '—'
              const days = c.days_between != null ? c.days_between.toFixed(1) : '—'
              const ml = c.ml_risk_score != null ? c.ml_risk_score.toFixed(1) : '—'
              return (
                <tr key={c.case_id} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-surface)]">
                  <td className="whitespace-nowrap px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => onOpenRelated(c.case_id)}
                      className="font-semibold text-[var(--accent-data)] hover:underline"
                    >
                      {c.case_id}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.import_date || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.export_date || '—'}</td>
                  <td className="max-w-[70px] truncate px-2 py-1.5" title={c.wagon || ''}>{c.wagon || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.import_shipment_id || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.export_shipment_id || '—'}</td>
                  <td className="max-w-[100px] truncate px-2 py-1.5" title={c.route}>{c.route || '—'}</td>
                  <td className="max-w-[80px] truncate px-2 py-1.5" title={c.import_station || ''}>{c.import_station || '—'}</td>
                  <td className="max-w-[80px] truncate px-2 py-1.5" title={c.export_station || ''}>{c.export_station || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.import_payer || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">{c.export_payer || '—'}</td>
                  <td className="px-2 py-1.5">{iw}</td>
                  <td className="px-2 py-1.5">{ew}</td>
                  <td className="px-2 py-1.5">{wd}</td>
                  <td className="px-2 py-1.5">{days}</td>
                  <td className="px-2 py-1.5 font-bold" style={{ color: scoreColor(c.anomaly_score) }}>{c.anomaly_score.toFixed(1)}</td>
                  <td className="px-2 py-1.5">{ml}</td>
                  <td className="max-w-[150px] truncate px-2 py-1.5 text-[var(--text-tertiary)]" title={c.difference_from_current}>{c.difference_from_current}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {c.methods.map(m => (
                      <span key={m} className="mr-1 inline-block rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-1.5 py-0.5 text-[9px] font-medium text-[var(--text-secondary)]">{m}</span>
                    ))}
                  </td>
                  <td className="max-w-[100px] truncate px-2 py-1.5 text-[var(--text-tertiary)]" title={humanReasons(c.a_reasons ?? c.short_causes, t)}>{humanReasons(c.a_reasons ?? c.short_causes, t) || '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!showAll && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 text-[11px] font-medium text-[var(--accent-data)] hover:underline"
        >
          Показать ещё {Math.min(hiddenCount, 5)}
        </button>
      )}
    </Section>
  )
}

function RuleScenarioSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const primary = d.primaryRuleScenario
  const matched = d.matchedRuleScenarios ?? []
  // Официальный сценарий кейса = source_scenario_id. Правиловые совпадения ниже — это
  // проверка по совпавшим признакам, они НЕ переопределяют сценарий кейса.
  const sourceScenarioId = d.source_scenario_id ?? null

  return (
    <Section title={t('analytics.section.ruleScenario')}>
      {sourceScenarioId != null && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[4px] px-3 py-2" style={{ background: 'rgba(59,130,246,.08)', borderLeft: '3px solid #3b82f6' }}>
          <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.rule.officialScenario')}</span>
          <span className="inline-block rounded bg-[var(--accent-data)] px-2 py-0.5 text-[12px] font-bold text-white">
            Сценарий {sourceScenarioId}
          </span>
          <span className="text-[11px] text-[var(--text-tertiary)]">
            {t('analytics.drawer.rulesNote')}
          </span>
        </div>
      )}

      {!primary ? (
        <div className="rounded-[4px] px-3 py-2.5" style={{ background: 'rgba(136,136,136,.08)', borderLeft: '3px solid #888' }}>
          <p className="text-[13px] font-semibold text-[var(--text-primary)]">{t('analytics.rule.notFullyMatched')}</p>
          <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
            Кейс имеет частичную похожесть {d.matchedScenarioFeaturesCount ?? d.scenario_like_score}/{d.totalScenarioFeaturesCount || 6},
            но не выполняет полный набор условий ни одного из правил 1–6.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-block rounded border border-[var(--accent-data)] px-2 py-0.5 text-[12px] font-bold text-[var(--accent-data)]">
              Правило {primary.scenarioId}
            </span>
            <span className="text-[13px] text-[var(--text-secondary)]">— {primary.name}</span>
          </div>

          {(() => {
            const main = matched.find(m => m.scenarioId === primary.scenarioId)
            if (!main) return null
            return (
              <>
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.rule.matchedSigns')}</p>
                  <div className="space-y-1">
                    {main.required.map(f => (
                      <div key={f.key} className="flex items-center gap-2 text-[11px]">
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <span className="text-[var(--text-secondary)]">{f.label}</span>
                        {f.value != null && <span className="text-[var(--text-tertiary)]">({f.value})</span>}
                      </div>
                    ))}
                  </div>
                </div>
                {main.notRequired.length > 0 && (
                  <p className="text-[11px] text-[var(--text-tertiary)]">
                    Не требуется для правила {main.scenarioId}: {main.notRequired.join(' / ')}
                  </p>
                )}
              </>
            )
          })()}

          {matched.filter(m => m.scenarioId !== primary.scenarioId).length > 0 && (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.rule.alsoPasses')}</p>
              <div className="space-y-1">
                {matched.filter(m => m.scenarioId !== primary.scenarioId).map(m => (
                  <div key={m.scenarioId} className="text-[11px] text-[var(--text-secondary)]">
                    Правило {m.scenarioId} — {m.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function ScenarioSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  if (d.scenario_like_score == null) return null
  const total = d.totalScenarioFeaturesCount || 6
  const matchedCount = d.matchedScenarioFeaturesCount ?? d.scenario_like_score
  const features = d.scenarioFeatureMatches ?? []
  const matched = features.filter(f => f.matched)
  const notMatched = features.filter(f => !f.matched)

  let slsText: string
  if (matchedCount <= 2) slsText = `Совпало ${matchedCount} из ${total} сценарных признаков — слабая похожесть.`
  else if (matchedCount <= 4) slsText = `Совпало ${matchedCount} из ${total} сценарных признаков — средняя похожесть.`
  else slsText = `Совпало ${matchedCount} из ${total} сценарных признаков — сильная похожесть на скрытый транзит.`

  return (
    <Section title={t('analytics.section.scenarioSimilarity')}>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[22px] font-bold text-[var(--text-primary)]">{matchedCount}/{total}</span>
        <span className="text-[12px] text-[var(--text-secondary)]">{d.scenario_label}</span>
      </div>
      <p className="text-[12px] text-[var(--text-secondary)] mb-3">{slsText}</p>

      {!d.scenarioFeaturesAvailable || features.length === 0 ? (
        <p className="text-[12px] text-[var(--text-tertiary)]">
          {t('analytics.drawer.scenarioDetailsUnavailable')}
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-tertiary)]">
              Совпавшие признаки {matched.length > 0 && `(${matched.length})`}
            </p>
            {matched.length === 0 ? (
              <p className="text-[11px] text-[var(--text-tertiary)]">{t('analytics.scenario.noMatchedSigns')}</p>
            ) : (
              <div className="space-y-1">
                {matched.map(f => (
                  <div key={f.key} className="flex items-center gap-2 text-[11px]">
                    <span style={{ color: '#22c55e' }}>✓</span>
                    <span className="text-[var(--text-secondary)]">{f.label}</span>
                    {f.value != null && <span className="text-[var(--text-tertiary)]">({f.value})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {notMatched.length > 0 && (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-tertiary)]">
                Не совпали ({notMatched.length})
              </p>
              <div className="space-y-1">
                {notMatched.map(f => (
                  <div key={f.key} className="flex items-center gap-2 text-[11px]">
                    <span style={{ color: '#888' }}>✗</span>
                    <span className="text-[var(--text-tertiary)]">{f.label}</span>
                    {f.value != null && <span className="text-[var(--text-tertiary)]">({f.value})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function CaseDetailsSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const f = useFormat()
  return (
    <Section title={t('analytics.section.caseDetails')}>
      <div className="grid grid-cols-2 gap-x-8 gap-y-0.5">
        <KV label={t('analytics.details.caseId')} value={d.case_id} highlight />
        <KV label={t('analytics.details.type')} value="pair" />
        <KV label={t('analytics.details.importDate')} value={esc(d.import_date)} />
        <KV label={t('analytics.details.exportDate')} value={esc(d.export_date)} />
        <KV label={t('analytics.details.cargo')} value={esc(d.cargo)} />
        <KV label={t('analytics.details.route')} value={esc(d.route)} />
        <KV label={t('analytics.details.importWeight')} value={d.import_weight != null ? `${f.number(d.import_weight, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('analytics.hist.tonneShort')}` : '—'} />
        <KV label={t('analytics.details.exportWeight')} value={d.export_weight != null ? `${f.number(d.export_weight, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('analytics.hist.tonneShort')}` : '—'} />
        <KV label={t('analytics.details.weightDelta')} value={d.weight_diff_pct != null ? `${d.weight_diff_pct.toFixed(1)}%` : '—'} />
        <KV label={t('analytics.details.daysBetween')} value={d.days_between != null ? `${d.days_between.toFixed(1)}` : '—'} />
        <KV label={t('analytics.details.importShipment')} value={esc(d.import_shipment_id)} />
        <KV label={t('analytics.details.exportShipment')} value={esc(d.export_shipment_id)} />
        <KV label={t('analytics.details.importWagon')} value={esc(d.import_wagon)} />
        <KV label={t('analytics.details.exportWagon')} value={esc(d.export_wagon)} />
        <KV label={t('analytics.details.payerCodeImport')} value={esc(d.import_payer)} />
        <KV label={t('analytics.details.payerCodeExport')} value={esc(d.export_payer)} />
        <KV label={t('analytics.details.binReceiverImport')} value={esc(d.import_receiver_bin)} />
        <KV label={t('analytics.details.binSenderExport')} value={esc(d.export_sender_bin)} />
        <KV label={t('analytics.details.countryFrom')} value={esc(d.import_departure_country)} />
        <KV label={t('analytics.details.countryTo')} value={esc(d.export_destination_country)} />
        <KV label={t('analytics.details.borderIn')} value={esc(d.import_border_station)} />
        <KV label={t('analytics.details.borderOut')} value={esc(d.export_border_station)} />
        <KV label={t('analytics.details.destStationImport')} value={esc(d.import_station)} />
        <KV label={t('analytics.details.depStationExport')} value={esc(d.export_station)} />
        <KV label={t('analytics.details.binMatches')} value={d.same_bin === 1 ? t('common.value.yes') : d.same_bin === 0 ? t('common.value.no') : '—'} />
        <KV label={t('analytics.details.payerMatches')} value={d.same_payer === 1 ? t('common.value.yes') : d.same_payer === 0 ? t('common.value.no') : '—'} />
        <KV label={t('analytics.details.stationMatches')} value={d.same_station === 1 ? t('common.value.yes') : d.same_station === 0 ? t('common.value.no') : '—'} />
        <KV label={t('analytics.details.gngMatches')} value={d.same_gng === 1 ? t('common.value.yes') : d.same_gng === 0 ? t('common.value.no') : '—'} />
      </div>
    </Section>
  )
}

function ChecklistSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  if (!d.analyst_checklist.length) return null
  return (
    <Section title={t('analytics.section.checklist')}>
      <div className="space-y-2">
        {d.analyst_checklist.map((item, i) => (
          <div key={i} className="flex gap-3 rounded-[4px] border border-[var(--border-default)] p-3">
            <span className="mt-0.5 shrink-0 text-[12px]">☐</span>
            <div>
              <p className="text-[12px] text-[var(--text-primary)]">{item.text}</p>
              <p className="mt-0.5 text-[10px] text-[var(--text-tertiary)]">{item.reason}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}

function RecommendationSection({ d }: { d: AnomalyDetail }) {
  const t = useT()
  const bgColor = d.anomaly_score >= 7
    ? 'rgba(239,68,68,.06)' : d.anomaly_score >= 3
    ? 'rgba(245,158,11,.06)' : 'rgba(34,197,94,.06)'
  const borderColor = d.anomaly_score >= 7
    ? '#ef4444' : d.anomaly_score >= 3
    ? '#f59e0b' : '#22c55e'
  return (
    <Section title={t('analytics.section.recommendation')}>
      <div className="rounded-[4px] px-4 py-3" style={{ background: bgColor, borderLeft: `3px solid ${borderColor}` }}>
        <p className="text-[13px] text-[var(--text-primary)]">{d.recommendation}</p>
      </div>
    </Section>
  )
}
